/**
 * @file Public env for Auth0 SPA + Help API audience (build-time `VITE_*`).
 */

/** Use with Auth0 SPA (no refresh tokens — avoids audience/refresh binding issues for this app). */
export const SSO_AUTH0_SCOPE = "openid profile email";

function env(name: keyof ImportMetaEnv): string | undefined {
  const raw = import.meta.env[name];
  return typeof raw === "string" ? raw.trim() || undefined : undefined;
}

export function ssoAppOrigin(): string {
  const raw = env("VITE_APP_URL") ?? "http://localhost:3000";
  return raw.replace(/\/$/, "");
}

/** True if `aud` looks like Auth0 OIDC userinfo — invalid as Help API audience. */
export function isOidcUserinfoAudience(audience: string): boolean {
  const a = audience.trim().toLowerCase();
  return a.endsWith("/userinfo") || a.includes(".auth0.com/userinfo");
}

/**
 * Help API identifier — must match `AUTH0_API_AUDIENCE` on the API (Auth0 → Applications → APIs).
 * Never use `…/userinfo`; that is not your custom API and breaks token refresh.
 */
export function getSsoApiAudience(): string | undefined {
  const raw = env("VITE_AUTH0_API_AUDIENCE");
  if (!raw || isOidcUserinfoAudience(raw)) {
    return undefined;
  }
  return raw;
}

/**
 * Domain + client + audience set, and audience is not the OIDC userinfo URL.
 */
export function isSsoConfigured(): boolean {
  return Boolean(env("VITE_AUTH0_DOMAIN") && env("VITE_AUTH0_CLIENT_ID") && getSsoApiAudience());
}

/**
 * When audience env points at OIDC userinfo — show this instead of redirecting in circles.
 */
export function ssoAudienceMisconfigurationMessage(): string | null {
  const raw = env("VITE_AUTH0_API_AUDIENCE");
  if (!raw || !isOidcUserinfoAudience(raw)) {
    return null;
  }
  return `VITE_AUTH0_API_AUDIENCE must be your Auth0 API Identifier (Dashboard → Applications → APIs), not the userinfo URL (${raw}). Set the same value as AUTH0_API_AUDIENCE on the Help API, then clear site local storage and sign in again.`;
}

export function getAuth0Domain(): string | undefined {
  return env("VITE_AUTH0_DOMAIN");
}

export function getAuth0ClientId(): string | undefined {
  return env("VITE_AUTH0_CLIENT_ID");
}
